import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

const BLOCK_REASON_LABELS: Record<string, string> = {
  xero_disabled: "Xero integration is disabled",
  estimated_payment: "Payment uses estimated rates",
  doctor_not_in_xero: "Doctor is not linked to a Xero contact",
  unknown_la: "Local authority is unknown",
  xero_disconnected: "Xero is not connected",
  max_payment_exceeded: "Total payment exceeds the configured maximum",
  max_doctor_requests_per_day:
    "Doctor has exceeded the daily bill creation limit",
  max_patient_requests_per_day:
    "Patient has exceeded the daily bill creation limit",
  max_other_exceeded: "An 'other' line item exceeds the configured maximum",
  max_mileage_exceeded: "Mileage total exceeds the configured maximum",
};

interface ConfirmBillOverrideDialogProps {
  open: boolean;
  blockReasons: string[];
  onClose: () => void;
  onConfirm: () => void;
}

export function ConfirmBillOverrideDialog({
  open,
  blockReasons,
  onClose,
  onConfirm,
}: ConfirmBillOverrideDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = () => {
    setIsSubmitting(true);
    onConfirm();
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Override Bill Creation Rules</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          The following rules will be overridden:
        </DialogContentText>
        <List dense disablePadding>
          {blockReasons.map((reason) => (
            <ListItem key={reason} sx={{ py: 0.25 }}>
              <ListItemText
                primary={BLOCK_REASON_LABELS[reason] ?? reason}
                slotProps={{ primary: { variant: "body2" } }}
              />
            </ListItem>
          ))}
        </List>
        <DialogContentText sx={{ mt: 2 }}>
          Are you sure you want to create this Xero bill?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="warning"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Override and Create Bill"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
